import { CaretDown } from '@app/components/icons/caret-down';
import { DownloadIcon } from '@app/components/icons/download';
import { EjectNewIcon } from '@app/components/icons/eject-new';
import { ImageGalleryIcon } from '@app/components/icons/image-gallery';
import { ShareWithArrowIcon } from '@app/components/icons/share-with-arrow';
import { Tooltip } from '@app/components/tooltip';
import { useFlag } from '@app/feature-flags/use-flag';
import { hasEventEnded } from '@app/routes/organization/routes/events/utils';
import { getPathParams } from '@app/routes/utils';
import { getUserExternalId } from '@app/store/selectors';
import { useAppDispatch, useAppSelector } from '@app/store/store';
import {
  FLAG_ENABLE_CREATOR_LAB_DISCOVERY,
  FLAG_ENABLE_STANDALONE_STUDIO,
  FLAG_POST_EVENT_ENABLE_CREATOR_LAB_CONSOLIDATION,
  FLAG_POST_EVENT_REHEARSAL_BACKUP_RECORDING_EDIT,
} from '@app/utilities/flags';
import { Menu, Transition } from '@headlessui/react';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, { Fragment, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useRecordingAnalytics } from '../../hooks';
import { RecordingDownloadDialog } from '../../routes/editor/components/recording-download-dialog';
import {
  selectRecording,
  selectRecordingEventInfo,
  selectSelectedClip,
} from '../../selector';
import { Switch } from '../switch';

/**
 * Delete this component when the flag "post-event-org-recordings-upload" (a.k.a. FLAG_POST_EVENT_ORG_RECORDINGS_UPLOAD)
 * is enabled for everyone in Production
 * Jira: https://jira.ringcentral.com/browse/EVE-14134
 */
export const ShareMenu: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const recording = useAppSelector(selectRecording);
  const eventInfo = useAppSelector(selectRecordingEventInfo);
  const selectedClip = useAppSelector(selectSelectedClip);
  const userId = useAppSelector(getUserExternalId);
  const isPublishShareDisabled = selectedClip != null;
  const { org_id, recordingId } = getPathParams(['org_id', 'recordingId']);
  const { trackRecordingEvent } = useRecordingAnalytics();
  const isCreatorLabDiscoveryEnabled = useFlag(
    FLAG_ENABLE_CREATOR_LAB_DISCOVERY,
  );
  const isStandaloneStudioEnabled = useFlag(FLAG_ENABLE_STANDALONE_STUDIO);

  const isStandaloneStudioRecording =
    isStandaloneStudioEnabled &&
    recording?.category === 'main' &&
    recording?.event_id === null;
  const isRehearsalBackupEnabled = useFlag(
    FLAG_POST_EVENT_REHEARSAL_BACKUP_RECORDING_EDIT,
  );

  const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);

  const isContentComposerEnabled = useFlag(
    FLAG_POST_EVENT_ENABLE_CREATOR_LAB_CONSOLIDATION,
  );

  const shouldShowDownloadDialog = useCallback(() => {
    if (!isCreatorLabDiscoveryEnabled || isContentComposerEnabled) return false;
    if (!userId || !recordingId) return false;

    const WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000;
    const storageKey = `download-dialog-shown-${userId}-${recordingId}`;
    const lastShown = localStorage.getItem(storageKey);

    return !lastShown || Date.now() - parseInt(lastShown, 10) > WEEK_IN_MS;
  }, [
    isCreatorLabDiscoveryEnabled,
    isContentComposerEnabled,
    userId,
    recordingId,
  ]);

  const proceedWithDownload = useCallback(() => {
    if (recordingId) {
      dispatch({
        type: '[recording] log download click',
        payload: {
          recordingId,
        },
      });
    }

    if (recording?.download_url) {
      window.open(recording.download_url, '_blank');
    }
  }, [recording, dispatch, recordingId]);

  const saveDialogTimestamp = useCallback(() => {
    if (userId && recordingId) {
      const storageKey = `download-dialog-shown-${userId}-${recordingId}`;
      localStorage.setItem(storageKey, Date.now().toString());
    }
  }, [userId, recordingId]);

  const onDownloadClick = useCallback(() => {
    trackRecordingEvent('ch_recording_editor_share_download_clicked');

    // Check if we should show dialog (with early returns)
    if (shouldShowDownloadDialog()) {
      setIsDownloadDialogOpen(true);
      return;
    }

    // Otherwise download directly
    proceedWithDownload();
  }, [trackRecordingEvent, shouldShowDownloadDialog, proceedWithDownload]);

  const handleDownloadFromDialog = useCallback(() => {
    saveDialogTimestamp();
    setIsDownloadDialogOpen(false);
    proceedWithDownload();
  }, [saveDialogTimestamp, proceedWithDownload]);

  const handleGoToCreatorLab = useCallback(() => {
    saveDialogTimestamp();
    setIsDownloadDialogOpen(false);
    // Navigate with source in location state (for page view tracking)
    navigate(
      `/org/${org_id}/content/recordings/${recordingId}/lab/social-post`,
      { state: { source: 'download_dialog' } },
    );
  }, [saveDialogTimestamp, navigate, org_id, recordingId]);

  const onContentHubClick = useCallback(() => {
    trackRecordingEvent('ch_recording_editor_share_contentHub_clicked');
    navigate(`/org/${org_id}/content/hubs`);
  }, [navigate, org_id, trackRecordingEvent]);

  const onPublishClick = useCallback(() => {
    if (!recordingId || !recording) return;

    trackRecordingEvent('ch_recording_editor_share_publishReplay_clicked');

    dispatch({
      type: '[recording] toggle publish started',
      payload: {
        recordingId,
        published: !recording.published,
      },
    });
  }, [dispatch, recordingId, recording, trackRecordingEvent]);

  const isEventEnded = eventInfo
    ? hasEventEnded({
        expired: eventInfo.expired,
        status: eventInfo.status || '',
        timeEnd: eventInfo.timeEnd,
      })
    : false;

  return (
    <Tooltip
      tooltip={t('content-recording.publish-share-disabled-while-editing')}
      shouldShow={isPublishShareDisabled}
      childClassName="inline-block"
    >
      <div
        className={cx(
          'relative inline-block',
          isPublishShareDisabled && 'cursor-not-allowed',
        )}
      >
        <Menu
          as="div"
          className={cx(
            'relative inline-block text-left z-10',
            isPublishShareDisabled && 'opacity-60',
          )}
        >
          <div>
            <Menu.Button
              disabled={isPublishShareDisabled}
              onClick={() => {
                trackRecordingEvent('ch_recording_editor_share_clicked');
              }}
              className="flex items-center text-sm font-medium border border-solid border-gray-600 text-spring-neutral-b0 rounded-10 hover:bg-spring-neutral-b0/10 transition-colors overflow-hidden"
              data-testid="share-menu-button"
            >
              <div className="flex items-center gap-2 pl-4 pr-2 py-2 border-r border-gray-600">
                {t('content-recording.publish-and-share')}
                <ShareWithArrowIcon className="w-5 h-5" />
              </div>
              <div className="p-2">
                <CaretDown className="w-5 h-5" />
              </div>
            </Menu.Button>
          </div>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 mt-2 max-w-80 w-max origin-top-right rounded-lg bg-spring-neutral-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none border border-gray-600">
              <div className="p-2">
                {(recording?.category === 'main' ||
                  (['backup', 'rehearsal'].includes(
                    recording?.category ?? '',
                  ) &&
                    isRehearsalBackupEnabled)) &&
                  !isStandaloneStudioRecording && (
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={`${
                            active ? 'bg-gray-700' : ''
                          } group flex w-full flex-col items-start rounded-md px-2 py-3 text-sm font-bold text-white hover:bg-gray-700 transition-colors`}
                          onClick={onPublishClick}
                          data-testid="share-menu-publish-button"
                        >
                          <div className="flex w-full items-center justify-between mb-1">
                            <div className="flex items-center gap-3">
                              <EjectNewIcon className="h-4 w-4 text-white" />
                              {t('content-recording.publish-to-replay')}
                            </div>
                            <div className="ml-4">
                              <Switch checked={!!recording?.published} />
                            </div>
                          </div>
                          <div className="text-xxs text-left text-white justify-self-start mb-2 font-normal">
                            {recording?.published
                              ? t('content-recording.auto-deactivate-replay')
                              : t('content-recording.auto-activate-replay')}
                          </div>
                          {eventInfo && (
                            <div className="flex w-full items-start gap-3 py-2">
                              <div className="w-20 h-12 rounded-lg overflow-hidden bg-gray-600 flex-shrink-0">
                                {eventInfo.imageUrl ? (
                                  <img
                                    src={eventInfo.imageUrl}
                                    alt="Event thumbnail"
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full bg-gradient-to-br from-blue-400 to-blue-600" />
                                )}
                              </div>

                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-spring-neutral-b2 text-xs">
                                    Event
                                  </span>
                                  {isEventEnded && (
                                    <span className="px-1 text-xs font-medium bg-neutral-b1 text-spring-neutral-b2 rounded">
                                      Ended
                                    </span>
                                  )}
                                </div>
                                <div className="text-spring-neutral-b0 text-sm font-medium truncate justify-self-start max-w-full">
                                  {recording?.event_name}
                                </div>
                              </div>
                            </div>
                          )}
                        </button>
                      )}
                    </Menu.Item>
                  )}
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={`${
                        active ? 'bg-gray-700' : ''
                      } group flex w-full items-center rounded-md px-2 py-3 text-sm font-bold text-white hover:bg-gray-700 transition-colors`}
                      onClick={onContentHubClick}
                      data-testid="share-menu-content-hub-button"
                    >
                      <ImageGalleryIcon className="mr-3 h-4 w-4 text-white" />
                      {t('content-recording.hub')}
                      <span className="ml-2 p-1 flex items-center justify-center text-xs bg-spring-primary text-white rounded-full font-bold">
                        {t('content-recording.new-tag')}
                      </span>
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={`${
                        active ? 'bg-gray-700' : ''
                      } group flex w-full items-center rounded-md px-2 py-3 text-sm font-bold text-white hover:bg-gray-700 transition-colors`}
                      onClick={onDownloadClick}
                      data-testid="share-menu-download-button"
                    >
                      <DownloadIcon className="mr-3 h-4 w-4 text-white" />
                      {t('content-recording.download')}
                    </button>
                  )}
                </Menu.Item>
              </div>
            </Menu.Items>
          </Transition>

          <RecordingDownloadDialog
            isOpen={isDownloadDialogOpen}
            onDownload={handleDownloadFromDialog}
            onGoToCreatorLab={handleGoToCreatorLab}
          />
        </Menu>
      </div>
    </Tooltip>
  );
};
